import { Big, BigSource } from 'big.js';
import { useEffect, useState } from 'react';
import { VisuallyHidden } from 'react-aria';

import { useGetMarketExchangeRate, useQuoteTrade } from '@ping/api';
import { PortfolioTokenSelect } from '@ping/components';
import { calculate, t } from '@ping/helpers';
import {
  useAvailablePortfolioTokensOfSelectedMarket,
  useEstimatedWeightedTotal,
  useIsSelectedMarketNftCommodity,
  useIsSelectedMarketNonFractionalCommodity,
  useIsUserLoggedIn,
} from '@ping/hooks';
import { useMarketOrderConfirmationSignal } from '@ping/signals';
import { Button, Checkbox, ComboField, Divider, Modal, NumberField, Slider, Timer, Toast, Tooltip } from '@ping/uikit';
import { DateTime, format, normalize } from '@ping/utils';
import { isCommodities } from '@ping/utils/checkCompany';

import { useIsNormalPriceComparison, type useOrderFormCommonState } from '../helpers';

import { AmountNumberField } from './AmountNumberField';
import { FormStructure } from './FormStructure';
import style from './style.module.scss';

const ZERO = 0 as const;
const SLIDER_STEPS = 1000 as const;
const MIN_SLIDER_STEP = 0.00000001 as const;
const MARKET_ORDER_CONFIRMATION_TIMEOUT = 10 as const; // Seconds

enum ESliderStep {
  ZERO_PERCENT = 0,
  TWENTY_FIVE_PERCENT = 25,
  FIFTY_PERCENT = 50,
  SEVENTY_FIVE_PERCENT = 75,
  HUNDRED_PERCENT = 100,
}

interface IOrderFormStructureProps {
  instrument: string;
  isDisabled?: boolean;
  orderKind: IOrderKind;
  orderSide: IOrderSide;
  state: ReturnType<typeof useOrderFormCommonState>;
  isNormalPrice?: boolean;
  isUserLoggedIn?: boolean;
  percentage?: {
    value: string;
    range: string;
  };
  isMarketFixedRatio?: boolean;
}

//
// Helpers
const isStopPriceFieldAccessible = (type: IOrderKind) => ['stop-limit'].includes(type);
const isLimitPriceFieldAccessible = (type: IOrderKind) => ['stop-limit'].includes(type);
const isPriceFieldAccessible = (type: IOrderKind) => ['limit'].includes(type);
const isLimitOrder = (orderKind: IOrderKind) => orderKind !== 'market';
const isStopOrder = (orderKind: IOrderKind) => orderKind === 'stop-limit';

/**
 * @description It checks if a value is in the range of a minimum and maximum value.
 * @param {import('big.js').BigSource} value - The value to check if it is in the range.
 * @param {import('big.js').BigSource} min - The minimum value of the range.
 * @param {import('big.js').BigSource} max - The maximum value of the range.
 * @returns {boolean}
 */
const isInRange = (value: BigSource, min: BigSource, max: BigSource) => {
  const precise_value = Big(value);
  return precise_value.gte(min) && precise_value.lte(max);
};

/**
 * It calculates the step value for a slider based on a maximum amount.
 * @param {Big} maxAmount - `maxAmount` is a variable of type `Big` which represents the maximum amount
 * that can be selected on a slider.
 * @returns The function `getAmountSliderStep` returns a `Big` number representing the step value for a
 * slider based on the maximum amount and the number of slider steps defined in the constant
 * `SLIDER_STEPS`. If the calculated step value is less than the minimum slider step defined in the
 * constant `MIN_SLIDER_STEP`, the function returns `MIN_SLIDER_STEP`.
 */
export const getAmountSliderStep = (maxAmount: Big) => {
  const stepValue = maxAmount.div(SLIDER_STEPS);
  return stepValue.lt(MIN_SLIDER_STEP) ? Big(MIN_SLIDER_STEP) : stepValue;
};

const OrderActivationPriceField = ({ state, isDisabled }: Pick<IOrderFormStructureProps, 'state' | 'isDisabled'>) => {
  return (
    <div className={style['form-field']}>
      <ComboField invalid={Boolean(state.error.activationPrice)}>
        <NumberField
          name='activationPrice'
          label={t('Stop price')}
          magnet='end'
          isDisabled={isDisabled}
          value={state.activationPrice}
          minValue={String(state.pair.minPrice ?? '')}
          maxValue={state.max.activationPrice.toString()}
          placeholder={t('Please enter Stop price')}
          onChange={state.setActivationPrice}
          formatOptions={{ maximumFractionDigits: state.pair.priceScale }}
        />
        <ComboField.Divider />
        <ComboField.Box className={style['asset-name']} magnet='start'>
          {state.pair.quoteAsset.toUpperCase()}
        </ComboField.Box>
      </ComboField>

      <span className={style['form-message']} aria-hidden={!state.error.activationPrice}>
        {state.error.activationPrice}
      </span>
    </div>
  );
};

interface OrderLimitPriceFieldProps
  extends Pick<IOrderFormStructureProps, 'state' | 'isDisabled' | 'isMarketFixedRatio'> {
  deviationError?: string;
}

const OrderLimitPriceField = ({ state, isDisabled, isMarketFixedRatio, deviationError }: OrderLimitPriceFieldProps) => (
  <div className={style['form-field']}>
    <ComboField invalid={Boolean(state.error.limitPrice || deviationError)}>
      <NumberField
        name='price'
        label={isMarketFixedRatio ? t('Limit Price (Fixed Ratio)') : t('Limit price')}
        magnet='end'
        isDisabled={isDisabled || isMarketFixedRatio}
        value={isMarketFixedRatio ? '1' : state.limitPrice}
        minValue={isMarketFixedRatio ? '1' : String(state.pair.minPrice ?? '')}
        maxValue={isMarketFixedRatio ? '1' : state.max.limitPrice.toString()}
        placeholder={t('Please enter Limit price')}
        onChange={isMarketFixedRatio ? () => state.setLimitPrice('1') : state.setLimitPrice}
        formatOptions={{ maximumFractionDigits: state.pair.priceScale }}
      />
      <ComboField.Divider />
      <ComboField.Box className={style['asset-name']} magnet='start'>
        {state.pair.quoteAsset.toUpperCase()}
      </ComboField.Box>
    </ComboField>

    <span className={style['form-message']} aria-hidden={!(state.error.limitPrice || deviationError)}>
      {state.error.limitPrice || deviationError}
    </span>
  </div>
);

interface OrderPriceFieldProps
  extends Pick<
    IOrderFormStructureProps,
    'state' | 'isDisabled' | 'isNormalPrice' | 'isUserLoggedIn' | 'percentage' | 'isMarketFixedRatio'
  > {
  deviationError?: string;
}

const OrderPriceField = ({
  state,
  isDisabled,
  isNormalPrice,
  percentage,
  isUserLoggedIn,
  isMarketFixedRatio,
  deviationError,
}: OrderPriceFieldProps) => (
  <div className={style['form-field']}>
    <ComboField invalid={Boolean(state.error.price || deviationError)}>
      <NumberField
        name='price'
        label={isMarketFixedRatio ? t('Price (Fixed Ratio)') : t('Price')}
        magnet='end'
        isDisabled={isDisabled || isMarketFixedRatio}
        value={isMarketFixedRatio ? '1' : state.price}
        minValue={isMarketFixedRatio ? '1' : String(state.pair.minPrice ?? '')}
        maxValue={isMarketFixedRatio ? '1' : state.max.price.toString()}
        placeholder={t('Please enter price')}
        onChange={isMarketFixedRatio ? () => state.setPrice('1') : state.setPrice}
        formatOptions={{ maximumFractionDigits: state.pair.priceScale }}
      />
      <ComboField.Divider />
      {isUserLoggedIn && !isNormalPrice && state.price != '' ? (
        <Tooltip
          position='top'
          showTooltip={!isNormalPrice}
          contentClassName={style['tooltip-info']}
          childrenIcon={
            <ComboField.Box className={style['asset-name']} magnet='start'>
              {state.pair.quoteAsset.toUpperCase()}
            </ComboField.Box>
          }
        >
          {t('%s% %s than the best offer', format.exponential(percentage?.value), percentage?.range)}
        </Tooltip>
      ) : (
        <ComboField.Box className={style['asset-name']} magnet='start'>
          {state.pair.quoteAsset.toUpperCase()}
        </ComboField.Box>
      )}
    </ComboField>

    <span className={style['form-message']} aria-hidden={!(state.error.price || deviationError)}>
      {state.error.price || deviationError}
    </span>
  </div>
);

function OrderAmountField({
  state,
  isDisabled,
  isSellNonFractionalCommodity,
  hideAmountField,
}: Pick<IOrderFormStructureProps, 'state' | 'isDisabled'> & {
  isSellNonFractionalCommodity?: boolean;
  hideAmountField?: boolean;
}) {
  const availableTokens = useAvailablePortfolioTokensOfSelectedMarket();

  return (
    <>
      {!hideAmountField && (
        <div className={style['form-field']}>
          <ComboField invalid={Boolean(state.error.amount)}>
            <AmountNumberField
              label={t('Amount')}
              name='amount'
              magnet='end'
              isDisabled={isDisabled || state.isMaxButtonLoading || isSellNonFractionalCommodity}
              value={state.amount}
              minValue={String(state.pair.minAmount ?? '')}
              maxValue={state.max.amount.toString()}
              placeholder={
                !isSellNonFractionalCommodity ? t('Please enter amount') : t('Please select token from below')
              }
              onChange={state.setAmount}
              formatOptions={{ maximumFractionDigits: state.pair.amountScale }}
            />

            {!isSellNonFractionalCommodity && (
              <ComboField.Button
                data-visible={state.isMaxButtonVisible}
                isDisabled={isDisabled || state.isMaxButtonLoading || state.max.amount.eq(ZERO)}
                isLoading={state.isMaxButtonLoading}
                magnet='both'
                onPress={state.setAmountMax}
              >
                {t('MAX')}
              </ComboField.Button>
            )}

            <ComboField.Divider />
            <ComboField.Box className={style['asset-name']} magnet='start'>
              {state.pair.baseAsset.toUpperCase()}
            </ComboField.Box>
          </ComboField>

          <span className={style['form-message']} aria-hidden={!state.error.amount}>
            {state.error.amount}
          </span>
        </div>
      )}

      {isSellNonFractionalCommodity && (
        <PortfolioTokenSelect
          isMultiple
          label={t('Token IDs')}
          name='tokenIds'
          tokens={availableTokens}
          onChange={amount => state.setAmount(amount.toString())}
        />
      )}
    </>
  );
}

export const OrderFormStructure = ({ state, ...props }: IOrderFormStructureProps) => {
  const isMarketNonFractionalCommodity = useIsSelectedMarketNonFractionalCommodity();
  const isMarketNftCommodity = useIsSelectedMarketNftCommodity();
  const isSellNonFractionalCommodity = isMarketNonFractionalCommodity && props.orderSide === 'sell';
  const isCommoditiesMode = isCommodities();
  const hideAmountField = isCommoditiesMode && isMarketNftCommodity && props.orderSide === 'sell';

  const { isNormalPrice, percentage } = useIsNormalPriceComparison(state.price);
  const isUserLoggedIn = useIsUserLoggedIn();

  const { data: marketExchangeRate } = useGetMarketExchangeRate(
    { instrument: props.instrument },
    {
      query: {
        // Only enable when we have an instrument and a positive max deviation configured for this market
        enabled: Boolean(props.instrument) && Number(state.pair?.maxPriceDeviationPercent) > 0,
        refetchInterval: DateTime.seconds(30),
        select: data => {
          // Fallback to 0 if null/undefined - 0 means no limit to apply
          const min_valid_price = Big(data.min_valid_price ?? 0);
          const max_valid_price = Big(data.max_valid_price ?? 0);
          // Only consider it a valid deviation range if both values are > 0
          const has_deviation_range = min_valid_price.gt(0) && max_valid_price.gt(0);

          return {
            ...data,
            min_valid_price,
            max_valid_price,
            has_deviation_range,
          };
        },
      },
    }
  );

  const isLimitKind = isLimitOrder(props.orderKind);
  const isStopLimitKind = isStopOrder(props.orderKind);

  const limitOrderPrice = state.precise.price ?? Big(ZERO);
  const stopLimitOrderPrice = state.precise.limitPrice ?? Big(ZERO);

  const isLimitPriceOutOfRange =
    isLimitKind &&
    limitOrderPrice.gt(0) &&
    marketExchangeRate?.has_deviation_range &&
    !isInRange(limitOrderPrice, marketExchangeRate.min_valid_price, marketExchangeRate.max_valid_price);

  const isStopLimitPriceOutOfRange =
    isStopLimitKind &&
    stopLimitOrderPrice.gt(0) &&
    marketExchangeRate?.has_deviation_range &&
    !isInRange(stopLimitOrderPrice, marketExchangeRate.min_valid_price, marketExchangeRate.max_valid_price);

  const isPriceOutOfDeviationRange = isLimitPriceOutOfRange || isStopLimitPriceOutOfRange;

  const formattedDeviationMin = marketExchangeRate?.has_deviation_range
    ? format.number(normalize(marketExchangeRate.min_valid_price, state.pair.priceScale))
    : null;
  const formattedDeviationMax = marketExchangeRate?.has_deviation_range
    ? format.number(normalize(marketExchangeRate.max_valid_price, state.pair.priceScale))
    : null;
  const deviationErrorMessage = t('Must be between %s and %s', formattedDeviationMin, formattedDeviationMax);

  if (props.isMarketFixedRatio && (state.limitPrice !== '1' || state.price !== '1')) {
    state.setPrice('1');
    state.setLimitPrice('1');
  }

  const estimatedWeightedTotal = useEstimatedWeightedTotal(
    state.precise.amount,
    props.orderSide,
    state.pair.priceScale
  );

  const [marketConfirmationModalVisibility, setMarketConfirmationModalVisibility] = useState(false);
  const [isTimerPlaying, setIsTimerPlaying] = useState(false);
  const [quoteTradeEnabled, setQuoteTradeEnabled] = useState(false);
  const quoteTrade = useQuoteTrade(
    {
      Amount: state.amount as unknown as number,
      Instrument: props.instrument,
      IsBuy: props.orderSide === 'buy',
    },
    {
      query: {
        enabled: quoteTradeEnabled,
        onSuccess: () => {
          setIsTimerPlaying(true);
        },
        onError: err => {
          Toast.error({ title: (err.response.data as any).message || t('Something went wrong!') });
          setIsTimerPlaying(false);
          setMarketConfirmationModalVisibility(false);
        },
        onSettled: () => {
          setQuoteTradeEnabled(false);
        },
      },
    }
  );

  const updateDetailsOnTimerChange = (remaining: number) => {
    if (remaining !== ZERO) {
      return;
    }

    quoteTrade.refetch();
  };

  const enableQuoteTradeOnSubmit = () => {
    if (!useMarketOrderConfirmationSignal.getConfirmationNeeded()) {
      return;
    }

    if (props.isMarketFixedRatio) {
      setMarketConfirmationModalVisibility(true);
      return;
    }

    setQuoteTradeEnabled(true);
    setMarketConfirmationModalVisibility(true);
  };

  useEffect(() => {
    if (isMarketNftCommodity && state.amount === '') {
      state.setAmount('1');
    }
  }, [isMarketNftCommodity, state.amount]);

  return (
    <>
      <FormStructure.Root
        onSuccess={state.reset}
        marketPrice={quoteTrade.data?.expectedLastMatchPrice}
        id='order-form'
        isMarketFixedRatio={props.isMarketFixedRatio}
      >
        <input name='isLimit' value={isLimitOrder(props.orderKind).toString()} type='hidden' />
        <input name='isStop' value={isStopOrder(props.orderKind).toString()} type='hidden' />
        <input name='type' value={props.orderSide} type='hidden' />
        <input name='instrument' value={props.instrument} type='hidden' />

        <FormStructure.Group hidden={!isStopPriceFieldAccessible(props.orderKind)}>
          <OrderActivationPriceField state={state} isDisabled={props.isDisabled} />
        </FormStructure.Group>

        <FormStructure.Group hidden={!isLimitPriceFieldAccessible(props.orderKind)}>
          <OrderLimitPriceField
            state={state}
            isDisabled={props.isDisabled}
            isMarketFixedRatio={props.isMarketFixedRatio}
            deviationError={isStopLimitPriceOutOfRange ? deviationErrorMessage : undefined}
          />
        </FormStructure.Group>

        <FormStructure.Group hidden={!isPriceFieldAccessible(props.orderKind)}>
          <OrderPriceField
            state={state}
            isDisabled={props.isDisabled}
            isNormalPrice={isNormalPrice}
            isUserLoggedIn={isUserLoggedIn}
            isMarketFixedRatio={props.isMarketFixedRatio}
            percentage={percentage}
            deviationError={isLimitPriceOutOfRange ? deviationErrorMessage : undefined}
          />
        </FormStructure.Group>

        <FormStructure.Group>
          <OrderAmountField
            state={state}
            isDisabled={props.isDisabled}
            isSellNonFractionalCommodity={isSellNonFractionalCommodity}
            hideAmountField={hideAmountField}
          />
          {!hideAmountField && (
            <>
              <FormStructure.Balance balance={state.balance} pair={state.pair} orderSide={props.orderSide} />
              <Slider.Root
                className={style['amount-slider']}
                isDisabled={props.isDisabled || state.isAmountSliderDisabled || isSellNonFractionalCommodity}
                preciseMaxValue={state.max.amount}
                preciseStep={getAmountSliderStep(state.max.amount)}
                value={Number(state.amount)}
                scale={state.sliderScale}
                onChangeValue={value => state.setAmount(format.exponential(value))}
              >
                <VisuallyHidden>
                  <Slider.Label>{t('Amount slider')}</Slider.Label>
                </VisuallyHidden>
                <Slider.Track>
                  <Slider.Thumb />
                  <Slider.MileStones>
                    <Slider.MileStones.Item percent={ESliderStep.ZERO_PERCENT} />
                    <Slider.MileStones.Item percent={ESliderStep.TWENTY_FIVE_PERCENT} />
                    <Slider.MileStones.Item percent={ESliderStep.FIFTY_PERCENT} />
                    <Slider.MileStones.Item percent={ESliderStep.SEVENTY_FIVE_PERCENT} />
                    <Slider.MileStones.Item percent={ESliderStep.HUNDRED_PERCENT} />
                  </Slider.MileStones>
                </Slider.Track>
              </Slider.Root>
            </>
          )}
        </FormStructure.Group>

        <FormStructure.Divider />

        <FormStructure.Stats>
          <FormStructure.FeeRow
            pair={state.pair}
            feePercentage={calculate.order.totalFeePercentage(state.pair)}
            quoteData={quoteTrade.data}
            orderSide={props.orderSide}
          />

          <FormStructure.Stats.Cell>
            {props.orderKind === 'market' ? (
              <div className={style['total-text-market']}>
                <span>{t('Estimated Total')}</span>
                <span>({t('Based on the last trade')})</span>
              </div>
            ) : (
              t('Total')
            )}
          </FormStructure.Stats.Cell>
          <FormStructure.Stats.Cell className={style['total-stats-cell']}>
            {/* TODO: maybe we are able to move the "normalize" fn into "format.number" */}
            {/* "maximumFractionDigits" in "format.number" rounds the input number */}
            {format.number(normalize(state.precise.total, state.pair.priceScale))} {state.pair.quoteAsset.toUpperCase()}
          </FormStructure.Stats.Cell>
          {props.orderKind === 'market' && (
            <>
              <FormStructure.Stats.Cell>{t('Estimated Weighted Total')}</FormStructure.Stats.Cell>
              <FormStructure.Stats.Cell>
                {format.exponential(estimatedWeightedTotal)}&nbsp;
                {state.pair.quoteAsset.toUpperCase()}
              </FormStructure.Stats.Cell>
            </>
          )}
        </FormStructure.Stats>

        <FormStructure.Submit
          side={props.orderSide}
          text={`${props.orderSide.toUpperCase()} ${state.pair.baseAsset.toUpperCase()}`}
          isDisabled={state.isFormInvalid || props.isDisabled || isPriceOutOfDeviationRange}
          price={state.price}
          orderKind={props.orderKind}
          isNormalPrice={isNormalPrice}
          onMarketOrderSubmit={enableQuoteTradeOnSubmit}
        />
      </FormStructure.Root>

      <Modal.Root isOpen={marketConfirmationModalVisibility}>
        <Modal.Portal className={style['confirm-order-modal']}>
          <Modal.Header className={style['confirm-order-modal__header']}>
            <Modal.Title className={style['confirm-order-modal__title']}>{t('Confirm order')}</Modal.Title>
            <Modal.CloseButton
              className={style['confirm-order-modal__close']}
              onPress={() => setMarketConfirmationModalVisibility(false)}
            />
          </Modal.Header>

          <Modal.Content className={style['confirm-order-modal__content']}>
            <Modal.Title className={style['confirm-order-modal__title-mobile']}>{t('Confirm order')}</Modal.Title>

            <section className={style['confirm-order-modal__details']}>
              <header className={style['confirm-order-modal__details-heading']}>
                <h6 className={style['confirm-order-modal__details-title']}>{t('Review information')}</h6>
                {!props.isMarketFixedRatio && (
                  <Timer.Root
                    className={style['confirm-order-timer']}
                    duration={MARKET_ORDER_CONFIRMATION_TIMEOUT}
                    isPlaying={isTimerPlaying}
                    onUpdate={updateDetailsOnTimerChange}
                  >
                    <Timer.Indicator />
                    <Timer.Description className={style['confirm-order-timer__title']}>
                      {remaining => (
                        <>
                          {t('Quote updates in')}&nbsp;
                          <output className={style['confirm-order-timer__remaining']}>{t('%ss', remaining)}</output>
                        </>
                      )}
                    </Timer.Description>
                  </Timer.Root>
                )}
              </header>

              <dl className={style['confirm-order-modal__detail-list']}>
                <dt className={style['confirm-order-modal__detail-head']}>{t('Pair')}</dt>
                <dd className={style['confirm-order-modal__detail-data']}>
                  {props.instrument.toUpperCase().replace('_', '-')}
                </dd>

                <dt className={style['confirm-order-modal__detail-head']}>{t('Side')}</dt>
                <dd className={style['confirm-order-modal__detail-data']}>
                  {format.string.toTitleCase(props.orderSide)}
                </dd>

                <dt className={style['confirm-order-modal__detail-head']}>{t('Type of order')}</dt>
                <dd className={style['confirm-order-modal__detail-data']}>
                  {format.string.toTitleCase(props.orderKind)}
                </dd>

                <dt className={style['confirm-order-modal__detail-head']}>{t('Amount')}</dt>
                <dd className={style['confirm-order-modal__detail-data']}>
                  {props.isMarketFixedRatio ? state.amount : quoteTrade.data?.fillableAmount}{' '}
                  {state.pair.baseAsset.toUpperCase()}
                </dd>

                <dt className={style['confirm-order-modal__detail-head']}>{t('Price')}</dt>
                <dd className={style['confirm-order-modal__detail-data']}>
                  {props.isMarketFixedRatio
                    ? `1 ${state.pair.quoteAsset.toUpperCase()}`
                    : `${format.exponential(
                        new Big(quoteTrade.data?.volumeWeightedPrice || ZERO).toNumber()
                      )}${state.pair.quoteAsset.toUpperCase()}`}
                </dd>

                <dt className={style['confirm-order-modal__detail-head']}>{t('Fee')}</dt>
                <dd className={style['confirm-order-modal__detail-data']}>
                  {props.isMarketFixedRatio
                    ? `${calculate.order.totalFeePercentage(state.pair)}%`
                    : calculate.order.feeValue(state.pair, quoteTrade.data, props.orderSide)}
                </dd>

                <Divider className={style['confirm-order-modal__detail-divider']} />
                <Divider className={style['confirm-order-modal__detail-divider']} />

                <dt className={style['confirm-order-modal__detail-head']}>{t('Total estimate')}</dt>
                <dd className={style['confirm-order-modal__detail-data']}>
                  {props.isMarketFixedRatio
                    ? `${format.number(
                        normalize(state.precise.total, state.pair.priceScale)
                      )} ${state.pair.quoteAsset.toUpperCase()}`
                    : calculate.order.estimatedTotal(state.pair, quoteTrade.data, props.orderSide)}
                </dd>
              </dl>
            </section>

            <Checkbox
              text={t('Do not show this confirmation again')}
              onChange={event => useMarketOrderConfirmationSignal.setConfirmationNeeded(!event.target.checked)}
            />
          </Modal.Content>

          <Modal.Actions className={style['confirm-order-modal__actions']}>
            <Button
              className={style['confirm-order-modal__cancel']}
              type='secondary'
              size='large'
              uppercase
              onClick={() => setMarketConfirmationModalVisibility(false)}
            >
              {t('Cancel')}
            </Button>
            <Button
              className={style['confirm-order-modal__confirm']}
              htmlType='submit'
              form='order-form'
              size='large'
              uppercase
              onClick={() => setMarketConfirmationModalVisibility(false)}
            >
              {t('Confirm')}
            </Button>
          </Modal.Actions>
        </Modal.Portal>
      </Modal.Root>
    </>
  );
};
